import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import type { ItemBusca, ModeloDetalhe, ModeloItem, ModeloResumo, Orcamento } from "../../../shared/tipos";
import { api } from "../api/cliente";
import { chaves } from "../api/consultas";
import { podeEditar, useAuth } from "../auth/AuthProvider";
import { useAvisos } from "../components/Avisos";
import { useAdicionarItem } from "../components/orcamento";
import { Carregando, ErroCarregamento, formatarMoeda, lerNumero, semAutocompletar } from "../components/ui";
import { useIdOrcamento } from "./OrcamentoDetalhe";

export function OrcamentoModelos() {
  const id = useIdOrcamento();
  const { usuario } = useAuth();
  const { avisar, avisarErro } = useAvisos();
  const [busca, setBusca] = useState("");
  const [texto, setTexto] = useState("");
  const { data, isLoading, error } = useQuery({
    queryKey: chaves.modelos(id),
    queryFn: () => api<{ orcamento: Orcamento; modelos: ModeloResumo[] }>(`/api/orcamentos/${id}/modelos`),
  });

  const solicitar = useMutation({
    mutationFn: () => api<{ mensagem: string }>(`/api/orcamentos/${id}/modelos/solicitacoes`, { method: "POST", body: { texto } }),
    onSuccess: (r) => {
      avisar(r.mensagem);
      setTexto("");
    },
    onError: avisarErro,
  });

  if (isLoading) return <Carregando />;
  if (error || !data) return <ErroCarregamento erro={error} />;
  const termo = busca.trim().toLowerCase();
  const visiveis = data.modelos.filter((m) => !termo || `${m.nome} ${m.descricao ?? ""}`.toLowerCase().includes(termo));

  return (
    <>
      <p className="note">
        <Link to={`/orcamentos/${id}`}>&larr; Voltar ao orçamento</Link>
      </p>
      <h1>Modelos de Referência</h1>
      <p className="note">
        Checklists de itens que costumam compor cada tipo de serviço, levantados em fontes técnicas públicas (SINAPI, NBRs,
        manuais de companhias de saneamento, TCU — a fonte de cada item aparece ao abrir o modelo).{" "}
        <strong>Não são preços nem itens obrigatórios</strong>: você decide, item a item, se ele se aplica a esta obra.
        Quando incluir um item, busque o código correspondente na tabela oficial — o preço do orçamento sempre vem de lá.
      </p>

      <div className="card" style={{ maxWidth: 520 }}>
        <label className="form-row" style={{ flexDirection: "column", alignItems: "stretch", margin: 0 }}>
          Buscar modelo por tipo de serviço
          <input type="search" placeholder="ex: esgoto, água, elevatória..." value={busca} onChange={(e) => setBusca(e.target.value)} {...semAutocompletar} />
        </label>
      </div>

      <div className="grid grid-2">
        {visiveis.map((m) => (
          <Link key={m.id} className="card" to={`/orcamentos/${id}/modelos/${m.id}`} style={{ display: "block" }}>
            <h3 style={{ marginTop: 0 }}>{m.nome}</h3>
            <p className="note">{m.descricao}</p>
            <span className={`badge ${m.incluidos ? "badge-green" : "badge-muted"}`}>
              {m.incluidos}/{m.total} marcados como incluídos
            </span>
            {m.cotacao_externa > 0 && <span className="badge badge-warn">{m.cotacao_externa} item(ns) sem equivalente EMBASA</span>}
          </Link>
        ))}
      </div>
      {visiveis.length === 0 && <p className="note">Nenhum modelo encontrado com esse termo.</p>}

      {podeEditar(usuario) && (
        <div className="card" style={{ maxWidth: 520, marginTop: 20 }}>
          <h3 style={{ marginTop: 0 }}>Não encontrou o modelo do serviço que precisa?</h3>
          <p className="note">
            Descreva o tipo de serviço/obra abaixo. A solicitação fica registrada para que um novo checklist de referência seja
            pesquisado (fontes técnicas + tabela EMBASA) e adicionado à lista de modelos.
          </p>
          <form
            onSubmit={(ev: FormEvent) => {
              ev.preventDefault();
              solicitar.mutate();
            }}
          >
            <div className="form-row">
              <input type="text" required placeholder="ex: assentamento de manilha de concreto armado" style={{ flex: 1 }} value={texto} onChange={(e) => setTexto(e.target.value)} />
              <button type="submit" className="btn-orange" disabled={solicitar.isPending}>
                Solicitar modelo
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}

function ResultadoBuscaModelo({ orcamentoId, termoInicial }: { orcamentoId: number; termoInicial: string }) {
  const [termo, setTermo] = useState(termoInicial);
  const [termoBuscado, setTermoBuscado] = useState(termoInicial);
  const [quantidades, setQuantidades] = useState<Record<number, string>>({});
  const adicionar = useAdicionarItem(orcamentoId);
  const navigate = useNavigate();
  const { avisar } = useAvisos();

  const { data = [], isFetching } = useQuery({
    queryKey: ["buscar-embasa", orcamentoId, termoBuscado],
    queryFn: () =>
      api<ItemBusca[]>(`/api/modelos/buscar-embasa?${new URLSearchParams({ q: termoBuscado, orcamento_id: String(orcamentoId) })}`),
  });

  return (
    <>
      <div className="form-row">
        <label>
          Termo
          <input type="text" style={{ minWidth: 220 }} value={termo} onChange={(e) => setTermo(e.target.value)} {...semAutocompletar} />
        </label>
        <button type="button" onClick={() => setTermoBuscado(termo)}>
          Buscar
        </button>
      </div>
      <div className="tabela-rolagem">
      <table>
        <tbody>
          {!isFetching && data.length === 0 && (
            <tr>
              <td className="note">Nenhum item encontrado com esse termo — tente outra palavra-chave.</td>
            </tr>
          )}
          {data.map((r) => (
            <tr key={r.id}>
              <td>{r.codigo}</td>
              <td>{r.descricao}</td>
              <td>{r.unidade || ""}</td>
              <td className="num">{formatarMoeda(r.preco_unitario)}</td>
              <td style={{ whiteSpace: "nowrap" }}>
                <input
                  type="text"
                  style={{ width: 60 }}
                  value={quantidades[r.id] ?? "1"}
                  onChange={(e) => setQuantidades({ ...quantidades, [r.id]: e.target.value })}
                  {...semAutocompletar}
                />{" "}
                <button
                  type="button"
                  disabled={adicionar.isPending}
                  onClick={() =>
                    adicionar.mutate(
                      { item_id: r.id, quantidade: lerNumero(quantidades[r.id] ?? "1") },
                      {
                        onSuccess: () => {
                          avisar(`${r.codigo} adicionado ao orçamento.`);
                          navigate(`/orcamentos/${orcamentoId}`);
                        },
                      },
                    )
                  }
                >
                  + Add
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </>
  );
}

function LinhaModelo({ item, orcamentoId, podeGerenciar, aberto, onAlternar }: { item: ModeloItem; orcamentoId: number; podeGerenciar: boolean; aberto: boolean; onAlternar: () => void }) {
  const queryClient = useQueryClient();
  const { avisarErro } = useAvisos();
  const [incluido, setIncluido] = useState(Boolean(item.incluido));

  return (
    <>
      <tr>
        <td>
          <input
            type="checkbox"
            checked={incluido}
            disabled={!podeGerenciar}
            onChange={async (e) => {
              const novo = e.target.checked;
              setIncluido(novo);
              try {
                await api(`/api/orcamentos/${orcamentoId}/modelos/itens/${item.id}`, { method: "PUT", body: { incluido: novo } });
                queryClient.invalidateQueries({ queryKey: chaves.modelos(orcamentoId) });
              } catch (erro) {
                setIncluido(!novo);
                avisarErro(erro);
              }
            }}
          />
        </td>
        <td>
          {item.descricao}
          {item.requer_cotacao_externa && (
            <span className="badge badge-warn" title="Este item não consta da tabela oficial EMBASA — o valor deve ser gerado por cotação (fornecedor/mercado), não pela tabela.">
              Cotação Externa
            </span>
          )}
        </td>
        <td className="note">{item.fonte}</td>
        <td>
          {item.requer_cotacao_externa ? (
            <span className="note">Este item deve ser gerado por cotação — sem equivalente na tabela EMBASA.</span>
          ) : (
            podeGerenciar && (
              <button type="button" className="btn-ghost" onClick={onAlternar}>
                Buscar na tabela oficial
              </button>
            )
          )}
        </td>
      </tr>
      {aberto && !item.requer_cotacao_externa && (
        <tr>
          <td />
          <td colSpan={3}>
            <ResultadoBuscaModelo orcamentoId={orcamentoId} termoInicial={item.busca_sugerida ?? ""} />
          </td>
        </tr>
      )}
    </>
  );
}

export function OrcamentoModeloDetalhe() {
  const id = useIdOrcamento();
  const { modeloId } = useParams();
  const [abertoId, setAbertoId] = useState<number | null>(null);
  const { data, isLoading, error } = useQuery({
    queryKey: [...chaves.modelos(id), Number(modeloId)],
    queryFn: () => api<ModeloDetalhe>(`/api/orcamentos/${id}/modelos/${modeloId}`),
  });

  if (isLoading) return <Carregando />;
  if (error || !data) return <ErroCarregamento erro={error} />;

  return (
    <>
      <p className="note">
        <Link to={`/orcamentos/${id}/modelos`}>&larr; Voltar aos modelos</Link>
      </p>
      <h1>{data.modelo.nome}</h1>
      <p className="note">{data.modelo.descricao}</p>
      {!data.pode_gerenciar && <p className="note">Este orçamento pertence a outro usuário — a seleção de itens abaixo é somente leitura.</p>}

      <div className="card">
        <div className="tabela-rolagem">
          <table>
            <thead>
              <tr>
                <th style={{ width: 36 }}>Incluir?</th>
                <th>Item de composição (referência)</th>
                <th>Fonte</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data.itens.map((item) => (
                <LinhaModelo
                  key={item.id}
                  item={item}
                  orcamentoId={id}
                  podeGerenciar={data.pode_gerenciar}
                  aberto={abertoId === item.id}
                  onAlternar={() => setAbertoId(abertoId === item.id ? null : item.id)}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
